// LiveScanStrip — a thin sticky bar shown at the top of every page while
// a scan is live and the user is NOT on its detail page.
//
// Clicking anywhere on the bar (except the Abort button) navigates back to
// the scan detail page. The strip disappears once the scan reaches a
// terminal status.
import { abortScan, listScans, type Scan } from "@/api/client";
import { anyScanInFlight } from "@/lib/scan-status";
import { inFlightInterval } from "@/hooks/useInFlightPolling";
import { Button } from "@/design-system";
import { cn } from "@/lib/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useRouterState } from "@tanstack/react-router";
import { Loader2, Square } from "lucide-react";

function useLiveScan(): Scan | undefined {
  const { data } = useQuery({
    queryKey: ["scans", "recent"],
    queryFn: ({ signal }) => listScans(signal),
    refetchInterval: (q) =>
      inFlightInterval(q.state.data, (d) => anyScanInFlight(d.scans)),
  });
  return data?.scans.find(
    (s) => s.status === "queued" || s.status === "running",
  );
}

function scanDetailPath(scan: Scan): string {
  return `/targets/${scan.targetId}/scans/${scan.id}`;
}

function useIsOnScanPage(scan: Scan | undefined): boolean {
  return useRouterState({
    select: (s) => {
      if (!scan) return false;
      return s.location.pathname === scanDetailPath(scan);
    },
  });
}

interface AbortButtonProps {
  aborting: boolean;
  onAbort: () => void;
}

function AbortButton({ aborting, onAbort }: AbortButtonProps) {
  return aborting ? (
    <Button variant="danger" size="sm" disabled>
      <Loader2 size={13} strokeWidth={2.5} className="animate-spin" />
      Aborting…
    </Button>
  ) : (
    <Button
      variant="danger"
      size="sm"
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onAbort();
      }}
    >
      <Square size={12} strokeWidth={2.5} />
      Abort
    </Button>
  );
}

interface StripTone {
  dot: string;
  ring: string;
  label: string;
}

function stripTone(status: Scan["status"], aborting: boolean): StripTone {
  if (aborting) {
    return {
      dot: "bg-sev-high",
      ring: "ring-2 ring-sev-high/20",
      label: "Aborting",
    };
  }
  if (status === "running") {
    return {
      dot: "bg-fam-reliability",
      ring: "ring-2 ring-fam-reliability/20",
      label: "Running",
    };
  }
  return {
    dot: "border border-dashed border-fg-3 bg-transparent",
    ring: "",
    label: "Queued",
  };
}

export function LiveScanStrip() {
  const scan = useLiveScan();
  const isOnScanPage = useIsOnScanPage(scan);

  const queryClient = useQueryClient();
  const abort = useMutation({
    mutationFn: () => abortScan(scan!.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["scans", "recent"] });
    },
  });

  if (!scan || isOnScanPage) return null;

  const aborting = abort.isPending;
  const tone = stripTone(scan.status, aborting);

  return (
    <Link
      to="/targets/$targetId/scans/$scanId"
      params={{ targetId: scan.targetId, scanId: scan.id }}
      className={cn(
        "flex h-9 shrink-0 flex-row items-center gap-3 border-b border-border-1",
        "bg-bg-1 px-4 text-small text-fg-1 transition-colors hover:bg-bg-2",
      )}
    >
      {/* Pulse dot */}
      <span
        className={cn(
          "size-2 rounded-full shrink-0",
          tone.dot,
          tone.ring,
          scan.status === "running" && !aborting && "animate-pulse",
        )}
      />

      {/* Status + target */}
      <span className="flex flex-row items-baseline gap-1.5 min-w-0 flex-1">
        <span className="font-medium text-fg-1">{tone.label}</span>
        <span className="text-fg-3">·</span>
        <span className="truncate text-fg-2">{scan.targetDisplayName}</span>
        <span className="text-fg-4 font-mono text-mono-sm">{scan.displayId}</span>
      </span>

      {/* Abort */}
      <AbortButton aborting={aborting} onAbort={() => abort.mutate()} />
    </Link>
  );
}
